import { useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { Typography } from '@/components/typography/typography'
import { cn } from '@/libs/tw'
import { translate } from '@/utils/translator'

import { ALLOWED_COUNTRIES, useAddressSuggestion } from '../../hooks/useAddressSuggestion'
import { useValidationContext } from '../../hooks/useValidationContext'
import { FormValues } from '../../schema'
import { FormDropdownInput } from '../inputs/formDropdownInput'
import { FormTextInput } from '../inputs/formTextInput'
import { FormTypeAHead } from '../inputs/formTypeAHead'

const countryCodes = [
	'AF',
	'EG',
	'AL',
	'DZ',
	'AD',
	'AO',
	'AG',
	'AR',
	'AM',
	'AZ',
	'ET',
	'AU',
	'BS',
	'BH',
	'BD',
	'BB',
	'BE',
	'BZ',
	'BJ',
	'BT',
	'BO',
	'BA',
	'BW',
	'BR',
	'BN',
	'BG',
	'BF',
	'BI',
	'CL',
	'CN',
	'CR',
	'DK',
	'DE',
	'DM',
	'DO',
	'DJ',
	'EC',
	'SV',
	'CI',
	'ER',
	'EE',
	'SZ',
	'FJ',
	'FI',
	'FR',
	'GA',
	'GM',
	'GE',
	'GH',
	'GD',
	'GR',
	'GT',
	'GN',
	'GW',
	'GY',
	'HT',
	'HN',
	'IN',
	'ID',
	'IQ',
	'IR',
	'IE',
	'IS',
	'IL',
	'IT',
	'JM',
	'JP',
	'YE',
	'JO',
	'KH',
	'CM',
	'CA',
	'CV',
	'KZ',
	'QA',
	'KE',
	'KG',
	'KI',
	'CO',
	'KM',
	'CD',
	'CG',
	'KP',
	'KR',
	'XK',
	'HR',
	'CU',
	'KW',
	'LA',
	'LS',
	'LV',
	'LB',
	'LR',
	'LY',
	'LI',
	'LT',
	'LU',
	'MG',
	'MW',
	'MY',
	'MV',
	'ML',
	'MT',
	'MA',
	'MH',
	'MR',
	'MU',
	'MX',
	'FM',
	'MD',
	'MC',
	'MN',
	'ME',
	'MZ',
	'MM',
	'NA',
	'NR',
	'NP',
	'NZ',
	'NI',
	'NL',
	'NE',
	'NG',
	'MK',
	'NO',
	'OM',
	'AT',
	'TL',
	'PK',
	'PW',
	'PA',
	'PG',
	'PY',
	'PE',
	'PH',
	'PL',
	'PT',
	'RW',
	'RO',
	'RU',
	'SB',
	'ZM',
	'WS',
	'SM',
	'ST',
	'SA',
	'SE',
	'CH',
	'SN',
	'RS',
	'SC',
	'SL',
	'ZW',
	'SG',
	'SK',
	'SI',
	'SO',
	'ES',
	'LK',
	'KN',
	'LC',
	'VC',
	'ZA',
	'SD',
	'SS',
	'SR',
	'SY',
	'TJ',
	'TZ',
	'TH',
	'TG',
	'TO',
	'TT',
	'TD',
	'CZ',
	'TN',
	'TR',
	'TM',
	'TV',
	'UG',
	'UA',
	'HU',
	'UY',
	'UZ',
	'VU',
	'VA',
	'VE',
	'AE',
	'US',
	'GB',
	'VN',
	'BY',
	'CF',
	'CY',
]

const getCountryOptions = () =>
	countryCodes.map((code) => ({ value: code, label: translate(`country-${code}`) }))

const topCountryOptions = ['de', 'at', 'ch']

export const AddressForm = () => {
	const { setCountry } = useValidationContext()
	const {
		formState: { errors },
		trigger,
		getValues,
		control,
	} = useFormContext<FormValues>()

	const country = useWatch({ control, name: 'country' })
	const postCode = useWatch({ control, name: 'postCode' })
	const city = useWatch({ control, name: 'city' })

	const allowedCountries = topCountryOptions.includes(country?.toLowerCase())
		? (country?.toLowerCase() as ALLOWED_COUNTRIES)
		: undefined

	const { data, getAddressSuggestion } = useAddressSuggestion()

	const cities = data?.localities.map((location) => location.localityName)
	const streets = data?.localities.find(
		(location) => location.localityName?.toLowerCase() === city?.toLowerCase()
	)?.streetNames

	const handlePostCodeBlur = async () => {
		const isValid = await trigger('postCode')
		if (!isValid || !allowedCountries) return

		getAddressSuggestion({ zip: postCode, country: allowedCountries })
	}

	useEffect(() => {
		setCountry(country)
		if (!getValues('postCode')) return

		setTimeout(() => {
			trigger('postCode')
		}, 200)
	}, [country, getValues, postCode, setCountry, trigger])

	return (
		<div className={cn('flex flex-col gap-24')}>
			<Typography style={'h3'} element="h3">
				{translate('guest-data-address')}
			</Typography>
			<FormDropdownInput
				name="country"
				label={translate('guest-data-country')}
				required
				error={errors.country?.message}
				autoComplete="country"
				topOptions={getCountryOptions().filter((option) =>
					topCountryOptions.includes(option.value.toLowerCase())
				)}
				options={getCountryOptions().filter(
					(option) => !topCountryOptions.includes(option.value.toLowerCase())
				)}
			/>
			<div className={cn('grid grid-cols-1 gap-16 md:grid-cols-2')}>
				<FormTextInput
					name="postCode"
					label={translate('guest-data-postCode')}
					required
					error={errors.postCode?.message}
					autoComplete="postal-code"
					onBlur={handlePostCodeBlur}
				/>
				<FormTypeAHead
					name="city"
					label={translate('guest-data-city')}
					required
					error={errors.city?.message}
					autoComplete="address-level2"
					options={cities}
					minLength={0}
				/>
				<FormTypeAHead
					name="street"
					label={translate('guest-data-street')}
					required
					error={errors.street?.message}
					autoComplete="street-address"
					options={streets}
				/>
				<FormTextInput
					name="streetNumber"
					label={translate('guest-data-streetNumber')}
					required
					error={errors.streetNumber?.message}
					autoComplete="address-line1"
				/>
			</div>
		</div>
	)
}
